// src/moment/components/AsyncImage/KenBurnsAsyncImage.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { KenBurnsParams } from '@moment/net/softbox-types.ts';
import { ElAnimation } from '@pages/Root/utils/elAnimation.ts';
import { IImageData } from '@pages/Root/services/api/interfaces.ts';
import { AnimationDirection } from '@pages/Root/utils/interfaces.ts';
import AsyncImage from './AsyncImage';

interface KenBurnsAsyncImageProps {
  src: string;
  alt?: string;
  width?: number | string;
  height?: number | string;
  className?: string;
  asyncImageContainerClassName?: string;
  staticSized?: boolean;
  kenBurnsParams: KenBurnsParams;
  imageData: IImageData;
  activeAnimation: boolean;
}

export const KenBurnsAsyncImage = ({
                                     src,
                                     alt = '',
                                     width = '100%', // Default width for AsyncImage container
                                     height = '100%', // Default height for AsyncImage container
                                     className,
                                     asyncImageContainerClassName,
                                     staticSized = false,
                                     kenBurnsParams,
                                     imageData,
                                     activeAnimation,
                                   }: KenBurnsAsyncImageProps) => {
  const [isImageReadyForAnimation, setIsImageReadyForAnimation] = useState(false);
  const [animationInstance, setAnimationInstance] = useState<ElAnimation | null>(null);
  const [initialImageTransform, setInitialImageTransform] = useState<string | undefined>(undefined);

  const imgRef = useRef<HTMLImageElement>(null);
  const kenBurnsContainerRef = useRef<HTMLDivElement>(null);

  const handleImageLoaded = () => {
    setIsImageReadyForAnimation(true);
  };

  // Effect to calculate and set the initial transform for the image
  useEffect(() => {
    if (imageData && kenBurnsParams && kenBurnsContainerRef.current) {
      const container = kenBurnsContainerRef.current;
      const frameWidth = container.offsetWidth;
      const frameHeight = container.offsetHeight;

      if (frameWidth > 0 && frameHeight > 0) {
        // For this calculation, ElAnimation's calculateBackgroundImageAnimation
        // doesn't strictly need the actual imageElement, but its constructor does.
        // We pass a dummy element. Ideally, calculateBackgroundImageAnimation
        // could be a static method or utility.
        const dummyImgElement = document.createElement('img');
        const calculator = new ElAnimation(dummyImgElement, imageData, container, kenBurnsParams);

        const initialAnimConfig = calculator.calculateBackgroundImageAnimation(
          imageData,
          kenBurnsParams,
          true, // atStart = true
          frameWidth,
          frameHeight
        );
        setInitialImageTransform(initialAnimConfig.transform);
      } else {
        // Container might not have dimensions yet on first pass if CSS is complex
        setInitialImageTransform(undefined);
      }
    } else {
      setInitialImageTransform(undefined);
    }
    // This effect depends on imageData, kenBurnsParams, and the container being ready.
    // Adding container dimensions to dependencies ensures recalculation if they change.
  }, [imageData, kenBurnsParams, kenBurnsContainerRef.current?.offsetWidth, kenBurnsContainerRef.current?.offsetHeight]);

  // Effect to handle src changes: reset states and cleanup animation
  useEffect(() => {
    setIsImageReadyForAnimation(false);
    setInitialImageTransform(undefined); // Reset pre-calculated transform
    if (animationInstance) {
      animationInstance.destroy();
      setAnimationInstance(null);
    }
  }, [src]); // Only depends on src

  // Effect for initializing and managing the ElAnimation instance
  useEffect(() => {
    let currentAnimation: ElAnimation | null = null;
    if (
      isImageReadyForAnimation &&
      kenBurnsParams &&
      imgRef.current && // Actual image element from AsyncImage is ready
      kenBurnsContainerRef.current &&
      imageData
    ) {
      // If an instance exists from a previous render (e.g. params changed), destroy it.
      // The src change effect handles cleanup when src itself changes.
      if (animationInstance) {
        animationInstance.destroy();
      }

      currentAnimation = new ElAnimation(
        imgRef.current,
        imageData,
        kenBurnsContainerRef.current,
        kenBurnsParams
      );

      currentAnimation.setInitConfig(); // Applies initial state via Web Animations API
      setAnimationInstance(currentAnimation);
    }

    return () => {
      // Cleanup when component unmounts or dependencies change before re-run
      currentAnimation?.destroy();
      // If the instance being destroyed is the one in state, clear it.
      // This check helps if setAnimationInstance is somehow called outside this effect's lifecycle.
      if (animationInstance === currentAnimation && currentAnimation !== null) {
        setAnimationInstance(null);
      }
    };
  }, [
    isImageReadyForAnimation,
    src, // If src changes, this effect will re-run after the src effect cleans up
    kenBurnsParams,
    imageData,
    // Refs (imgRef, kenBurnsContainerRef) are stable; their .current is checked
  ]);

  const [animationStarted, setAnimationStarted] = useState(false)

  // Effect for controlling play/pause state of the animation
  useEffect(() => {
    if (!animationInstance) {return;}
    if (activeAnimation) {
      if (!animationStarted) {
        animationInstance.runAnimation(true, AnimationDirection.left);
        setAnimationStarted(true)
      }
      animationInstance.unpauseAnimation();
    } else {
      animationInstance.pauseAnimation();
    }
  }, [activeAnimation, animationInstance]);

  // Styles for the main Ken Burns container div
  const kenBurnsContainerStyle: React.CSSProperties = useMemo(() => {return {
    // width: '100vw', // Example fixed width
    // maxHeight: '70vh', // Example fixed height
    maxWidth: '450px',
    width: '100%', // Ensure container takes full width of its parent
    height: '100%', // Ensure container takes full height of its parent
    overflow: 'hidden',
    position: 'relative',
  }}, []);

  // Styles to be applied to the <img> tag via AsyncImage
  // This includes the pre-calculated initial transform.
  const imageStyleWithInitialTransform = useMemo(() => {return {
    transform: initialImageTransform,
    // transformOrigin: '0 0', // Or 'center center', depending on ElAnimation's logic. Default is 'center center'.
    // ElAnimation seems to calculate absolute transforms, so default origin should be fine.
    width: 'auto', // Let the image use its natural size, ElAnimation will scale it
    height: 'auto', // Let the image use its natural size, ElAnimation will scale it
    // objectFit: 'cover', // Consider if ElAnimation's scaling isn't already handling this.
    // ElAnimation calculates specific scale and translate, so this might be redundant or conflict.
    // It's usually better to let ElAnimation control the exact appearance.
  }}, [initialImageTransform]);

  return (
    <div
      ref={kenBurnsContainerRef}
      className={className}
      style={kenBurnsContainerStyle}
    >
      <AsyncImage
        ref={imgRef} // Forwarded to the actual <img> tag
        src={src}
        alt={alt}
        width={width} // Controls AsyncImage's own container sizing
        height={height} // Controls AsyncImage's own container sizing
        containerClassName={asyncImageContainerClassName}
        staticSized={staticSized}
        onImageLoad={handleImageLoaded}
        // Pass the initial transform style directly to the <img> tag
        style={imageStyleWithInitialTransform}
      />
    </div>
  );
};

export default KenBurnsAsyncImage;
